import { createSelector } from 'reselect';

import { SupportedTenantsEnum } from '../../constants/auth';
import { NO_DEPARTMENT } from '../../constants/organizations';
import { FALLBACK_CURRENCY_CODE } from '../../constants/shell';
import { DEPT_ADMIN, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN, USER } from '../../constants/users';
import { ViewTypesEnum } from '../../types/shell';
import { hasPermission } from '../../utils/users';
import { IAppState } from '../reducers';
import {
  selectAdminType,
  selectDigitalBudgetValue,
  selectIsDigitalBudgetLoading,
  selectUserDepartmentId,
} from './auth';
import { selectBucketIsFetching, selectIsBulkLoading } from './bucket';
import { selectIsCampaignsFetching } from './campaigns';
import { selectIsOrganizationSummariesFetching } from './dashboard';
import { selectIsFulfillmentCentersFetching } from './fulfillmentCenters';
import {
  selectCRMIsLoading,
  selectCRMIsLoadingOpportunities,
  selectIsEmailIntegrationLoading,
  selectZapierIsLoading,
} from './integrations';
import { selectIsInventoryItemLoading, selectLowInventoryItems } from './inventories';
import { selectIsSingleOneLinkFetching } from './oneLink';
import {
  selectCurrentDepartmentRemainingBudget,
  selectIsDepartmentBudgetEnabled,
  selectOrganizationsIsLoading,
} from './organizations';
import { selectIsBusinessReasonsLoading } from './reasons';
import { selectIsSingleDeliveryReportsFetching } from './reports';
import { selectIsSignatureLoading } from './signature';
import {
  selectEmailTemplatesIsLoading,
  selectIsClaimLinkTemplateLoading,
  selectIsClaimLinkTemplateSaving,
  selectNoteTemplatesIsLoading,
  selectTemplateIsLoading,
} from './templates';
import { selectEngagementIsLoading } from './tools';
import { selectIsUsersFetching } from './users';

export const selectRootState = (state: IAppState) => state.shell;

export const selectIsShowDigitalBudget = createSelector(
  selectDigitalBudgetValue,
  selectAdminType,
  (digitalBudget, adminType) => {
    const isExist = typeof digitalBudget !== 'undefined' && digitalBudget !== null;
    return isExist && hasPermission([ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN], adminType);
  },
);

export const selectIsShowDepartmentBudget = createSelector(
  selectIsDepartmentBudgetEnabled,
  selectCurrentDepartmentRemainingBudget,
  selectAdminType,
  selectUserDepartmentId,
  (isEnabled, departmentBudget, adminType, depId) => {
    const isExist = typeof departmentBudget !== 'undefined' && departmentBudget !== null;
    return isEnabled && isExist && hasPermission([USER, DEPT_ADMIN], adminType) && depId !== NO_DEPARTMENT;
  },
);

export const selectEmergencyNotification = createSelector(selectRootState, (state) => state.emergencyNotification);

export const selectCurrencySettings = createSelector(selectRootState, (state) => state.currencyRates || undefined);

export const selectShippingRules = createSelector(selectRootState, (state) => state.shippingRules || undefined);

export const selectTaxRequiredCountries = createSelector(
  selectShippingRules,
  (shippingRules) => shippingRules?.tax_required_countries,
);

export const selectDefaultExperienceBlueprint = createSelector(
  selectRootState,
  (state) => state.defaultExperienceBlueprint || undefined,
);

export const selectCurrencyCodes = createSelector(selectCurrencySettings, (currency) =>
  Object.keys(currency?.rates || []),
);

export const selectCurrencyRates = createSelector(selectCurrencySettings, (currency) => currency?.rates || {});

export const selectBaseCurrency = createSelector(
  selectCurrencySettings,
  (currency) => currency?.base_curency || FALLBACK_CURRENCY_CODE,
);

export const selectIsShowContentLoader = createSelector(
  [
    selectBucketIsFetching,
    selectIsBulkLoading,
    selectIsUsersFetching,
    selectIsSingleDeliveryReportsFetching,
    selectIsSingleOneLinkFetching,
    selectCRMIsLoadingOpportunities,
    selectCRMIsLoading,
    selectNoteTemplatesIsLoading,
    selectEmailTemplatesIsLoading,
    selectTemplateIsLoading,
    selectIsEmailIntegrationLoading,
    selectIsBusinessReasonsLoading,
    selectIsDigitalBudgetLoading,
    selectZapierIsLoading,
    selectOrganizationsIsLoading,
    selectEngagementIsLoading,
    selectIsCampaignsFetching,
    selectIsInventoryItemLoading,
    selectIsFulfillmentCentersFetching,
    selectIsSignatureLoading,
    selectIsOrganizationSummariesFetching,
    selectIsClaimLinkTemplateLoading,
    selectIsClaimLinkTemplateSaving,
  ],
  (...flags: any) => flags.some((flag: boolean) => flag),
);

export const selectIsSidenavShown = createSelector(selectRootState, (shell) => (shell ? shell.sidenavIsShown : false));

export const selectIsBoxDetailsSidebarShown = createSelector(selectRootState, (shell) =>
  shell ? shell.boxDetailsSidebarIsShown : false,
);

export const selectIsDrawerShown = createSelector(selectRootState, (shell) => (shell ? shell.drawerIsShown : false));

export const selectIs2FADrawerShown = createSelector(selectRootState, (shell) =>
  shell ? shell.twoFASidenavIsShown : false,
);

export const selectIsFeedbackSidebarShown = createSelector(selectRootState, (shell) =>
  shell ? shell.feedbackSidenavIsShown : false,
);

export const selectIsSuggestGiftSidebarShown = createSelector(selectRootState, (shell) =>
  shell ? shell.suggestGiftSidenavIsShown : false,
);

export const selectMetadata = createSelector(selectRootState, (shell) => (shell ? shell.metadata : undefined));

export const selectTheme = createSelector(selectRootState, (shell) => (shell ? shell.theme : undefined));

export const selectCrmProviders = createSelector(selectMetadata, (metadata) =>
  metadata ? metadata.crm_providers : undefined,
);

export const selectEmailProviders = createSelector(selectMetadata, (metadata) =>
  metadata ? metadata.email_providers : undefined,
);

export const selectDelayedShippingBaseUrl = createSelector(selectMetadata, (metadata) =>
  metadata ? metadata.delayed_shipping_base_url : undefined,
);

export const selectFlags = createSelector(selectRootState, (shell) => shell.flags);

export const selectIsOrganizationFormExpanded = createSelector(
  selectFlags,
  (flags) => flags.isOrganizationFormExpanded,
);

export const selectDelayedShippingTextCustomizationTags = createSelector(selectMetadata, (metadata) =>
  metadata ? metadata.ds_text_customization_tags : null,
);

export const selectIsMskuOptionsFormShown = createSelector(selectFlags, (flags) => flags.isMskuOptionsFormShown);

export const selectIsAIExtensionInstalled = createSelector(selectFlags, (flags) => flags.isAIExtensionInstalled);

export const selectLowItemsSnoozedAt = createSelector(selectRootState, (shell) => shell.lowItemsSnoozedAt);

export const selectLowItemsShownAt = createSelector(selectRootState, (shell) => shell.lowItemsLastShownAt);

export const selectShouldShowLowInventoryWarning = createSelector(selectLowInventoryItems, (items) => !!items?.length);

export const selectAppTenant = createSelector(selectRootState, (shell) => shell.tenant);

export const selectIsJaneStreetTenant = createSelector(
  selectAppTenant,
  (tenant) => tenant === SupportedTenantsEnum.JaneStreet,
);

export const selectIsPaceTenant = createSelector(selectAppTenant, (tenant) => tenant === SupportedTenantsEnum.Pace);

export const selectIsVercelTenant = createSelector(selectAppTenant, (tenant) => tenant === SupportedTenantsEnum.Vercel);

export const selectIsDeelTenant = createSelector(selectAppTenant, (tenant) => tenant === SupportedTenantsEnum.Deel);

export const selectIsConsumerReportsTenant = createSelector(
  selectAppTenant,
  (tenant) => tenant === SupportedTenantsEnum.ConsumerReports,
);

export const selectIsKeysightTenant = createSelector(
  selectAppTenant,
  (tenant) => tenant === SupportedTenantsEnum.Keysight,
);

export const selectIsDocusignTenant = createSelector(
  selectAppTenant,
  (tenant) => tenant === SupportedTenantsEnum.Docusign,
);

export const selectIsImprintEngineTenant = createSelector(
  selectAppTenant,
  (tenant) => tenant === SupportedTenantsEnum.ImprintEngine,
);

export const selectReportsActiveView = createSelector(selectRootState, (shell) =>
  shell ? shell.reportsActiveView : ViewTypesEnum.List,
);

export const selectUsersActiveView = createSelector(selectRootState, (shell) =>
  shell ? shell.usersActiveView : ViewTypesEnum.List,
);

export const selectOneLinksActiveView = createSelector(selectRootState, (shell) =>
  shell ? shell.oneLinksActiveView : ViewTypesEnum.List,
);

export const selectInventoryActiveView = createSelector(selectRootState, (shell) =>
  shell ? shell.inventoryActiveView : ViewTypesEnum.List,
);
